import React from 'react';
import { Typography } from '@mui/material';
import {
  ArticleBulletList,
  ArticleParagraph,
  ArticleSection,
  ArticleSubsection,
  ArticleTable,
} from './article-shared';

export const CptCodingGuideArticle: React.FC = () => (
  <>
    <ArticleSection title="Overview">
      <ArticleParagraph>
        CPT (Current Procedural Terminology) codes are standardized billing codes that describe the type and
        complexity of medical and therapeutic services provided. These codes allow BetterHelp to submit claims
        accurately to insurance payers and ensure your work is properly represented and reimbursed.
      </ArticleParagraph>
      <ArticleParagraph>
        You&apos;ll select CPT codes within the Client Session Note after each visit. Code selection depends on:
      </ArticleParagraph>
      <ArticleBulletList
        items={[
          'Whether the patient is new or established',
          'Whether coding is based on time and/or medical decision-making (MDM)',
          'Whether the visit included additional and dedicated psychotherapy',
        ]}
      />
    </ArticleSection>

    <ArticleSection title="New Patient Evaluation Codes (99202–99205)">
      <ArticleParagraph>
        Used when seeing a patient for the first time or after more than 12 months since the previous evaluation,
        depending on time spent or medical complexity.
      </ArticleParagraph>
      <ArticleTable
        headers={[ 'Code', 'Time-Based coding (Minutes)', 'Complexity - Level coding', 'Clinical Description' ]}
        rows={[
          [ '99202', '15–29', 'Straightforward', 'Simple problem; minimal data review or risk, typically self limiting.' ],
          [ '99203', '30–44', 'Low', 'Typically one stable issue; limited data review and minimal risk, no acute concerns' ],
          [ '99204', '45–59', 'Moderate', 'Multiple issues or medication decisions requiring evaluation and monitoring' ],
          [ '99205', '60–74', 'High', 'Complex diagnostic or management decisions; high risk of morbidity, crisis intervention' ],
        ]}
      />
      <ArticleParagraph>
        Additional stand-alone evaluation codes:
      </ArticleParagraph>
      <ArticleTable
        headers={[ 'Code', 'Description' ]}
        rows={[
          [ '90792', 'Stand alone code for a new patient evaluation WITH medical services. No add on codes allowed; therapy services are already built into the code.' ],
          [ '90791', 'Stand alone code for a diagnostic new patient evaluation with NO medical services provided. Cannot use add on codes.' ],
        ]}
      />
      <ArticleSubsection title="Tips">
        <ArticleBulletList
          items={[
            'For complexity-based coding, thoroughly document presenting problems, psychiatric review of systems, risk evaluation and conclusions, and other relevant medical considerations completed during the visit.',
            'For time-based coding, time spent is face to face and takes into account medical evaluation and discussion (not psychotherapy). If psychotherapy was provided, use the therapy add-on codes below.',
          ]}
        />
      </ArticleSubsection>
    </ArticleSection>

    <ArticleSection title="Established Patient Follow-Up Codes (99212–99215)">
      <ArticleParagraph>
        Used for ongoing or follow-up psychiatric visits. You may again use time-based or complexity-based coding.
      </ArticleParagraph>
      <ArticleTable
        headers={[ 'Code', 'Time-Based Range (Minutes)', 'Complexity Level', 'Clinical Description' ]}
        rows={[
          [ '99212', '10–19', 'Straightforward', 'Brief follow-up, minimal care needed, typically self limiting problem' ],
          [ '99213', '15–24', 'Low', 'Typically a single stable condition, routine management, possible refill needed' ],
          [ '99214', '30–39', 'Moderate', 'Multiple diagnosis, medication changes, active/worsening symptoms, labs or coordination needed' ],
          [ '99215', '40–54', 'High', 'High risk decision-making, complex polypharmacy, acute risk of death or injury, consideration of PHP or hospitalization, crisis coordination' ],
        ]}
      />
      <ArticleParagraph>
        For follow-ups involving significant psychotherapy, pair these codes with psychotherapy add-on codes.
      </ArticleParagraph>
    </ArticleSection>

    <ArticleSection title="Psychotherapy Add-On Codes">
      <ArticleBulletList
        items={[
          <>Add-on codes are used when psychotherapy is a <strong>distinct and significant</strong> portion of the visit.</>,
          'They cannot be billed alone — they must accompany an E/M (evaluation & management) code.',
        ]}
      />
      <ArticleTable
        headers={[ 'Code', 'Duration (Minutes)', 'Typically Used With', 'Example' ]}
        rows={[
          [ '90833', '16–37', '99213 / 99214', 'Medication management + brief therapy' ],
          [ '90836', '38–52', '99214 / 99215', 'Medication management + extended therapy' ],
          [ '90837', '53+', 'Standalone (therapy only)', 'Full psychotherapy session' ],
          [ '90839', '60 min for first code', 'Crisis evaluation', 'Psychotherapy for acute crisis stabilization' ],
          [ '90840', 'Additional 30 minutes for second code', 'Crisis evaluation', 'Psychotherapy for acute crisis stabilization' ],
        ]}
      />
      <ArticleSubsection title="Key Reminders">
        <ArticleBulletList
          items={[
            'Add-on codes require clear separation between therapy and medical decision making in documentation.',
            'Track and document the time spent on therapy separately from medication management.',
            'Document modality of therapy and focus (e.g., Supportive psychotherapy, CBT for anxiety, trauma-focused processing). Stating the goals of the therapy is advised.',
          ]}
        />
      </ArticleSubsection>
    </ArticleSection>

    <ArticleSection title="Common Coding Combinations">
      <ArticleTable
        headers={[ 'Scenario', 'CPT Code Combination', 'Notes' ]}
        rows={[
          [ 'Initial psychiatric evaluation', '90792', 'Includes history, medical assessment and therapy services.' ],
          [ 'New patient + therapy', '99204 + 90833', 'Moderate complexity or 45–59 minutes total, add on therapy' ],
          [ 'Established patient + brief therapy', '99214 + 90833', 'Medication management + supportive therapy' ],
          [ 'Established patient + extended therapy', '99214 + 90836', 'If therapy ≥ 38 minutes, in addition to medication services for moderately complex presentation.' ],
          [ 'Psychotherapy only (no med management)', '90834 / 90837', 'Time based Therapy-only sessions (38+ and 52+ min).' ],
          [ 'Screening tools (PHQ-9, GAD-7, etc.)', '96127', 'Can use for screening tools reviewed/administered during the session; must document the tool.' ],
        ]}
      />
    </ArticleSection>

    <ArticleSection title="Key Coding Considerations">
      <ArticleBulletList
        items={[
          '90792 should only be billed once under BetterHelp, unless the patient has had a significant lapse in care from BetterHelp and is re-establishing care (e.g., 12+ months).',
          'Therapy add-on codes (e.g., 90833, 90836) cannot be billed with 90792.',
          'Therapy add-on codes cannot be billed without an E/M code.',
        ]}
      />
      <Typography variant="body1" sx={{ mt: 2 }}>
        Ensure diagnoses and documentation support the complexity of the code chosen. Higher level coding requires
        thorough documentation to capture the services you&apos;ve provided.
      </Typography>
    </ArticleSection>
  </>
);
